import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';

interface DuplicateUploadDialogProps {
  open: boolean;
  /** Names of the duplicate files awaiting the user's decision. */
  fileNames: string[];
  /** Upload the duplicates anyway. */
  onConfirm: () => void;
  /** Skip the duplicates (fresh files from the same drop are unaffected). */
  onCancel: () => void;
}

/**
 * Confirmation shown when a user adds files whose names already exist — either
 * in the wizard's selected list or among the case's documents. Rendered as a
 * plain fixed overlay (same pattern as CaseWizardModal) so it stacks above the
 * wizard's own z-[100] backdrop.
 */
export function DuplicateUploadDialog({
  open,
  fileNames,
  onConfirm,
  onCancel,
}: DuplicateUploadDialogProps) {
  if (!open || fileNames.length === 0) return null;

  const plural = fileNames.length > 1;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[120]"
      role="alertdialog"
      aria-modal="true"
      aria-label="Duplicate files"
    >
      <div className="bg-white rounded-lg w-[440px] max-h-[70vh] overflow-hidden flex flex-col shadow-xl">
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <h2 className="text-lg font-semibold">
            {plural ? 'Files already exist' : 'File already exists'}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          <p className="text-sm text-gray-700">
            {plural
              ? `These ${fileNames.length} files have already been added to this case. Are you sure you want to upload them again?`
              : 'This file has already been added to this case. Are you sure you want to upload it again?'}
          </p>
          <ul className="space-y-1 max-h-[160px] overflow-y-auto">
            {fileNames.map((name, i) => (
              <li
                key={`${name}-${i}`}
                className="truncate rounded bg-gray-50 border border-gray-200 px-2 py-1 text-xs text-gray-800"
              >
                {name}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <Button variant="outline" onClick={onCancel}>
            Skip {plural ? 'them' : 'it'}
          </Button>
          <Button onClick={onConfirm}>Upload anyway</Button>
        </div>
      </div>
    </div>
  );
}
